/**
 * 批次列表筛选组件
 *
 * 放在 CollapsibleFilterBar 的 Drawer 中展开。
 * 采用实时自动搜索模式（onValuesChange + 防抖），无需手动点击搜索按钮。
 *
 * 支持的筛选维度（与后端 GET /ap/batches API 对齐）：
 *   - vendor_id: 供应商（单选，后端只接受单个 int）
 *   - cost_type: 费用类型/预分类（单选，后端只接受单个 string）
 *   - airport_code: 机场代码（单选，后端只接受单个 string）
 *
 * 后端暂不支持的维度（已标注，未实现）：
 *   - 日期范围
 *   - 批次级状态筛选
 *
 * 引用数据策略：
 *   供应商列表由 Page 层统一加载并通过 supplierOptions prop 传入，
 *   本组件是纯 UI 执行者，不自行发起数据请求。
 */
import React, { useRef, useEffect } from "react";
import { Form, Select, Row, Col } from "antd";
import { STANDARD_COST_TYPE_OPTIONS } from "../../constants";
import AirportSelector from "../../../../components/common/AirportSelector";

const ApInvBatchFilter = ({ onSearch, onReset, loading, supplierOptions = [], creatorOptions = [], currentUserId }) => {
    const [form] = Form.useForm();

    // 防抖引用
    const debounceRef = useRef(null);

    // 将表单值转换为后端 API 参数
    const transformFormToApiParams = (values) => {
        const params = {};
        if (values.vendor_id != null) params.vendor_id = values.vendor_id;
        if (values.cost_type) params.cost_type = values.cost_type;
        if (values.airport_code) params.airport_code = values.airport_code;
        if (values.created_by_user_id != null) params.created_by_user_id = values.created_by_user_id;
        return params;
    };

    // 表单值变化 — 防抖后自动提交
    const handleFormChange = (_changedValues, allValues) => {
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => {
            const params = transformFormToApiParams(allValues);
            onSearch?.(params);
        }, 500);
    };

    // 重置表单 — 恢复默认值（含创建人默认选中自己）
    const handleReset = () => {
        if (debounceRef.current) clearTimeout(debounceRef.current);
        form.resetFields();
        onReset?.();
    };

    // 清理定时器
    useEffect(() => {
        return () => {
            if (debounceRef.current) clearTimeout(debounceRef.current);
        };
    }, []);

    return (
        <Form
            form={form}
            layout="vertical"
            initialValues={{ created_by_user_id: currentUserId }}
            onValuesChange={handleFormChange}
        >
            <Row gutter={16}>
                <Col xs={24} sm={12} md={8}>
                    <Form.Item label="供应商" name="vendor_id">
                        <Select
                            placeholder="选择供应商"
                            allowClear
                            showSearch
                            options={supplierOptions}
                            filterOption={(input, option) =>
                                (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                            }
                            notFoundContent="暂无供应商"
                        />
                    </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={8}>
                    <Form.Item label="费用类型（预分类）" name="cost_type">
                        <Select
                            placeholder="选择费用类型"
                            allowClear
                            showSearch
                            options={STANDARD_COST_TYPE_OPTIONS}
                            filterOption={(input, option) =>
                                (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                            }
                        />
                    </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={8}>
                    <Form.Item label="机场" name="airport_code">
                        <AirportSelector
                            valueType="iata_code"
                            placeholder="选择机场"
                            allowClear
                            style={{ width: "100%" }}
                        />
                    </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={8}>
                    <Form.Item label="创建人" name="created_by_user_id">
                        <Select
                            placeholder="全部用户"
                            allowClear
                            showSearch
                            options={creatorOptions}
                            filterOption={(input, option) =>
                                (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                            }
                            notFoundContent="暂无用户数据"
                        />
                    </Form.Item>
                </Col>
            </Row>
        </Form>
    );
};

export default ApInvBatchFilter;
